/**
 * Beat 8, part 2 — Otto's empty pen, the fuse, the hens, and the torch.
 *
 * Spec §7.8 in full: *"Cut the barn's power from outside (the fuse box is at boot
 * height, which is head height for her). Free the hens. They scatter uselessly,
 * and some do not make it. First lesson: saving is messy."*
 *
 * The stage list is `docs/beat-8-plan.md`'s table, and two of its rules are
 * load-bearing enough to restate here because they are what a later edit will
 * break first:
 *
 *  - **`pass-pen` must not be gated on looking at the pen.** A stage that waits
 *    for the player to notice Otto is gone turns the one room-dress in this game
 *    that is a character's whole payoff into a fetch quest, and stalls the beat
 *    for anybody who walks past it. If they miss it, they miss it. The beat's
 *    restraint has to include being missable.
 *  - **Every stage has a patience.** Four of these wait on the player and all
 *    four have a backstop, because an Act 2 boot that cannot finish is a save the
 *    player cannot get out of.
 */

import type { Beat, Stage, StoryContext } from '@/story/types';

export const BLACKOUT_HALL_IDS = {
  fuse: 'blackout.fuse',
  /** One per openable crate. `BLACKOUT_OPEN_CRATES` decides how many there are. */
  crateA: 'blackout.crate-a',
  crateB: 'blackout.crate-b',
} as const;

export const BLACKOUT_HALL_FLAGS = {
  passedPen: 'blackout.passedPen',
  fuseThrown: 'blackout.fuseThrown',
  /** Every bird that got out has run itself to a stop. Raised by the flock. */
  flockSettled: 'blackout.flockSettled',
  /** He has gone back out through the far door. Raised by the torch director. */
  torchGone: 'blackout.torchGone',
} as const;

export const BLACKOUT_HALL_CUES = {
  open: 'blackout.open',
  offerFuse: 'blackout.offerFuse',
  throwFuse: 'blackout.throwFuse',
  afterDark: 'blackout.afterDark',
  /** The crates become reachable only once the hall is dark. */
  offerCrates: 'blackout.offerCrates',
  /** He comes down the alley. */
  torchIn: 'blackout.torchIn',
  fadeOut: 'blackout.fadeOut',
  end: 'blackout.end',
} as const;

function hold(id: string, seconds: number, ...cues: readonly string[]): Stage {
  return { id, enter: (_ctx, emit) => cues.forEach((cue) => emit(cue)), done: (ctx) => ctx.stageTime >= seconds };
}

function waitFor(id: string, flag: string, patience: number, ...cues: readonly string[]): Stage {
  return {
    id,
    enter: (_ctx, emit) => cues.forEach((cue) => emit(cue)),
    done: (ctx: StoryContext) => ctx.completed.has(flag) || ctx.stageTime >= patience,
  };
}

/**
 * The crates get the longest patience in the beat, and 70 s is not generous.
 *
 * *"The player will chase some of them and it will not work"* — the plan says so,
 * and chasing eight birds around a dark alley is the point of the stage rather
 * than a way of failing it. The backstop exists so a player who does not touch a
 * crate at all still reaches Kestner; it is not a pace the stage expects.
 */
const CRATE_PATIENCE = 70;
/** He takes about eighteen seconds. This is that plus a wide margin, not a pace. */
const TORCH_PATIENCE = 40;
const FADE_SECONDS = 4;

export const BLACKOUT_HALL_BEAT: Beat = {
  number: 8,
  id: 'act2.blackoutHall',
  stages: [
    hold('opening', 0.7, BLACKOUT_HALL_CUES.open),
    waitFor('pass-pen', BLACKOUT_HALL_FLAGS.passedPen, 25),
    waitFor('fuse', BLACKOUT_HALL_FLAGS.fuseThrown, 45, BLACKOUT_HALL_CUES.offerFuse),
    hold('dark', 1.5, BLACKOUT_HALL_CUES.throwFuse),
    waitFor('crates', BLACKOUT_HALL_FLAGS.flockSettled, CRATE_PATIENCE, BLACKOUT_HALL_CUES.afterDark, BLACKOUT_HALL_CUES.offerCrates),
    waitFor('torch', BLACKOUT_HALL_FLAGS.torchGone, TORCH_PATIENCE, BLACKOUT_HALL_CUES.torchIn),
    {
      id: 'close',
      enter: (_ctx, emit): void => emit(BLACKOUT_HALL_CUES.fadeOut),
      done: (ctx: StoryContext): boolean => ctx.stageTime >= FADE_SECONDS,
      exit: (_ctx, emit): void => emit(BLACKOUT_HALL_CUES.end),
    },
  ],
};
